import { allocate } from '@fd/money';
import type { CashFlowSummary } from './cashflow.ts';

/**
 * The cash-flow diagram as data (sources → one hub → targets): the diagram and its table view.
 * Every value is in the base currency; the sides always balance:
 *   Σ sources = hub = Σ targets.
 * Sources: income categories (+ "from balance" when more went out than came in).
 * Targets: spending categories, and savings & investments — with what stayed on the accounts
 * among them, account by account (the owner, 30.09.2026: "it is wealth too").
 */
/** "elsewhere": what the period left after spending and savings but no account holds any more. */
export type FlowNodeKind = 'income' | 'fromBalance' | 'expense' | 'otherExpense' | 'savings' | 'surplus' | 'elsewhere';

export type FlowNode = {
  /** Stable key: the category id, or a fixed key for the synthetic nodes. */
  readonly key: string;
  readonly kind: FlowNodeKind;
  readonly categoryId: string | null;
  /** Spending of a whole group of categories (D-035): the group, and categoryId stays null. */
  readonly groupId?: string | null;
  /** Income: whose it is (the one owner of the account it came into). */
  readonly personId?: string | null;
  /** Savings: the savings or investment account the money went to. */
  readonly accountId?: string | null;
  readonly valueMinor: bigint;
  /** For "otherExpense": how many categories were folded in. */
  readonly folded?: number;
  /** A group's own categories that spent, largest first: the column after the group. */
  readonly children?: readonly FlowNode[];
};

export type FlowGraph = {
  readonly sources: readonly FlowNode[];
  readonly targets: readonly FlowNode[];
  readonly hubMinor: bigint;
};

export function buildFlowGraph(
  summary: CashFlowSummary,
  options: {
    maxExpenseTargets?: number;
    maxIncomeSources?: number;
    /** The group a spending category belongs to: its spending is drawn once for the group. */
    groupOf?: (categoryId: string | null) => string | null;
    /** Whose income an account receives: income is then drawn per category and person. */
    earnerOf?: (accountId: string) => string | null;
  } = {},
): FlowGraph {
  const maxTargets = options.maxExpenseTargets ?? 7;
  const maxSources = options.maxIncomeSources ?? 4;
  const booked = summary.booked;

  const earnerOf = options.earnerOf;
  const perPerson = new Map<string, { categoryId: string | null; personId: string | null; incomeMinor: bigint }>();
  if (earnerOf) {
    for (const c of summary.incomeByAccount) {
      const personId = earnerOf(c.accountId);
      const key = `${c.categoryId ?? 'income:none'}|${personId ?? ''}`;
      const entry = perPerson.get(key) ?? { categoryId: c.categoryId, personId, incomeMinor: 0n };
      entry.incomeMinor += c.incomeMinor;
      perPerson.set(key, entry);
    }
  }
  const incomes = (
    earnerOf
      ? [...perPerson.entries()].map(([key, c]) => ({ key, ...c }))
      : summary.incomeByCategory.map((c) => ({ key: c.categoryId ?? 'income:none', categoryId: c.categoryId, personId: null, incomeMinor: c.incomeMinor }))
  )
    .filter((c) => c.incomeMinor > 0n)
    .sort((a, b) => (a.incomeMinor !== b.incomeMinor ? (a.incomeMinor > b.incomeMinor ? -1 : 1) : a.key < b.key ? -1 : 1));
  const sources: FlowNode[] = incomes.slice(0, maxSources).map((c) => ({
    key: c.key,
    kind: 'income',
    categoryId: c.categoryId,
    ...(c.personId ? { personId: c.personId } : {}),
    valueMinor: c.incomeMinor,
  }));
  const restIncome = incomes.slice(maxSources).reduce((sum, c) => sum + c.incomeMinor, 0n);
  if (restIncome > 0n) sources.push({ key: 'income:other', kind: 'income', categoryId: null, valueMinor: restIncome });

  // A group's categories are added up first (a refund in one nets against the others).
  const grouped = new Map<string, { categoryId: string | null; groupId: string | null; netSpendMinor: bigint; parts: Map<string | null, bigint> }>();
  for (const c of summary.byCategory) {
    const groupId = options.groupOf?.(c.categoryId) ?? null;
    const key = groupId ? `group:${groupId}` : (c.categoryId ?? 'expense:none');
    const entry = grouped.get(key) ?? { categoryId: groupId ? null : c.categoryId, groupId, netSpendMinor: 0n, parts: new Map() };
    entry.netSpendMinor += c.netSpendMinor;
    entry.parts.set(c.categoryId, (entry.parts.get(c.categoryId) ?? 0n) + c.netSpendMinor);
    grouped.set(key, entry);
  }
  // A category that took in more refunds than it spent has nothing to draw.
  const childrenOf = (key: string, parts: ReadonlyMap<string | null, bigint>): FlowNode[] =>
    [...parts.entries()]
      .filter(([, value]) => value > 0n)
      .map(([categoryId, value]) => ({ key: `${key}|${categoryId ?? 'none'}`, kind: 'expense' as const, categoryId, valueMinor: value }))
      .sort((a, b) => (a.valueMinor !== b.valueMinor ? (a.valueMinor > b.valueMinor ? -1 : 1) : a.key < b.key ? -1 : 1));
  const spending = [...grouped.entries()]
    .map(([key, entry]) => ({ key, ...entry }))
    .filter((c) => c.netSpendMinor > 0n)
    .sort((a, b) => (a.netSpendMinor !== b.netSpendMinor ? (a.netSpendMinor > b.netSpendMinor ? -1 : 1) : a.key < b.key ? -1 : 1));
  // Folding a single category into "Other" hides it for nothing — show it instead.
  const shown = spending.length === maxTargets + 1 ? maxTargets + 1 : maxTargets;
  const targets: FlowNode[] = spending.slice(0, shown).map((c) => ({
    key: c.key,
    kind: 'expense',
    categoryId: c.categoryId,
    ...(c.groupId ? { groupId: c.groupId, children: childrenOf(c.key, c.parts) } : {}),
    valueMinor: c.netSpendMinor,
  }));
  const rest = spending.slice(shown);
  const restSpend = rest.reduce((sum, c) => sum + c.netSpendMinor, 0n);
  if (restSpend > 0n) targets.push({ key: 'expense:other', kind: 'otherExpense', categoryId: null, valueMinor: restSpend, folded: rest.length });

  // Category nets can exceed the total when some categories are net-negative (refund-heavy);
  // use the drawn values so both sides balance exactly.
  const outSpend = targets.reduce((sum, t) => sum + t.valueMinor, 0n);
  const inIncome = sources.reduce((sum, s) => sum + s.valueMinor, 0n);
  const savings = booked.savingsContributionMinor > 0n ? booked.savingsContributionMinor : 0n;
  const outflow = outSpend + savings;
  const surplus = inIncome > outflow ? inIncome - outflow : 0n;
  // What stayed, on the accounts that really kept money this period (never more than each holds):
  // the surplus shared by what each kept when they kept at least as much; else what each kept,
  // and the rest — gone in settlements, transfers or payments outside the categories — on its own.
  const kept = summary.stayedByAccount.filter((a) => a.stayedMinor > 0n);
  const keptMinor = kept.reduce((sum, a) => sum + a.stayedMinor, 0n);
  const known = summary.stayedByAccount.length > 0;
  const stayedMinor = !known ? surplus : keptMinor < surplus ? keptMinor : surplus;
  const shares = keptMinor >= surplus && surplus > 0n && kept.length > 0 ? allocate(surplus, kept.map((a) => a.stayedMinor)) : kept.map((a) => a.stayedMinor);
  const stayed: FlowNode[] =
    surplus === 0n
      ? []
      : !known
        ? [{ key: 'surplus', kind: 'surplus', categoryId: null, valueMinor: surplus }]
        : kept
            .map((a, i) => ({ key: `surplus|${a.accountId}`, kind: 'surplus' as const, categoryId: null, accountId: a.accountId, valueMinor: shares[i]! }))
            .filter((node) => node.valueMinor > 0n);
  if (savings > 0n || stayedMinor > 0n) {
    // Where it went, account by account (the owner: "which part to Trading 212, which to savings").
    const accounts: FlowNode[] = summary.savingsByAccount
      .filter((a) => a.contributionMinor > 0n)
      .map((a) => ({ key: `savings|${a.accountId}`, kind: 'savings' as const, categoryId: null, accountId: a.accountId, valueMinor: a.contributionMinor }));
    if (savings > 0n && accounts.length === 0) accounts.push({ key: 'savings|none', kind: 'savings', categoryId: null, valueMinor: savings });
    const children = [...accounts, ...stayed];
    targets.push({ key: 'savings', kind: 'savings', categoryId: null, valueMinor: savings + stayedMinor, ...(children.length > 0 ? { children } : {}) });
  }
  if (surplus > stayedMinor) targets.push({ key: 'elsewhere', kind: 'elsewhere', categoryId: null, valueMinor: surplus - stayedMinor });
  if (outflow > inIncome) {
    sources.push({ key: 'fromBalance', kind: 'fromBalance', categoryId: null, valueMinor: outflow - inIncome });
  }
  const hubMinor = sources.reduce((sum, s) => sum + s.valueMinor, 0n);
  return { sources, targets, hubMinor };
}
